import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, Transition } from "@headlessui/react";
import {
  AdjustmentsHorizontalIcon,
  BookOpenIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Select from "../../../components/forms/Select/Select";
import AsyncSearchSelect from "../../../components/forms/Select/AsyncSearchSelect";
import PassageChip from "../../../components/Passages/PassageChip";
import { DifficultyBadge } from "../../../components/Difficulty/Difficulty";
import usePassage, { forgetPassage } from "../../../components/Passages/usePassage";
import usePassagesAvailable from "../../../components/Passages/usePassagesAvailable";
import { getPassages } from "../../../services/passageService";
import {
  getObjectives,
  getPurposes,
  getQuestionFolders,
  getQuestions,
  getStatuses,
  getSubjects,
  getSubtopics,
  getTopics,
} from "../../../services/questionService";
import { isCanceledError } from "../../../utils/examError";
import {
  cardClass,
  focusRing,
  iconButtonClass,
  isDraft,
  isTestBlocked,
  linkButtonClass,
  questionMeta,
  Spinner,
  StatusBadge,
  tintButtonClass,
  useMediaQuery,
} from "./ui";

const PAGE_SIZE = 50;
const ALL = "all";
const EMPTY_FILTERS = { folder: ALL, subject: ALL, grade: ALL, topic: ALL, subtopic: ALL, status: ALL, purpose: ALL, objective: ALL, passage: ALL };
const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);
const valueOf = (v) => (v !== ALL ? v : undefined);

/**
 * "Add whole passage (n)" for a passage's detail: its questions that are not in the test (`inTest`),
 * in the passage's order and marked with the passage (and its folder), go to onAdd. Those its exam does not
 * take into tests yet (not approved, or refused by the server: `blockedIds`) stay out and are counted
 * beside it. Nothing to add: says so.
 */
export function AddPassageButton({ detail, inTest, onAdd, blockedIds }) {
  const [t] = useTranslation();
  const questions = (detail?.questions || []).filter((q) => !isDraft(q));
  if (!questions.length) return null;
  const passage = { uuid: detail.uuid, name: detail.name };
  // The passage's questions live in its folder and carry none of their own.
  const missing = [...questions]
    .sort((a, b) => (a.passage_order ?? 0) - (b.passage_order ?? 0))
    .filter((q) => q?.uuid && !inTest.has(q.uuid))
    .map((q) => ({ ...q, passage, folder: q.folder || detail.folder || null }));
  if (!missing.length) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-700">
        <CheckIcon className="h-4 w-4" aria-hidden="true" />
        {t("test_bank.builder.passage.all_in_test")}
      </span>
    );
  }
  const blocked = missing.filter((q) => isTestBlocked(q) || blockedIds?.has(q.uuid));
  const addable = missing.filter((q) => !blocked.includes(q));
  const blockedNote = blocked.length > 0 && (
    <span title={t("errors.not_approved")} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-slate-600">
      <LockClosedIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
      {t("test_bank.builder.passage.blocked_left", { count: blocked.length })}
    </span>
  );
  if (!addable.length) return blockedNote;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      <button
        type="button"
        onClick={(e) => {
          // The second click of a double click would add nothing new.
          if (e.detail > 1) return;
          onAdd(addable);
        }}
        className={tintButtonClass}
      >
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {blocked.length
          ? t("test_bank.builder.passage.add_approved", { count: addable.length })
          : t("test_bank.builder.passage.add_all", { count: addable.length })}
      </button>
      {blockedNote}
    </span>
  );
}

// The bank filtered by a passage: which one, and its questions added in one go.
function PassageFilterBanner({ passage, inTest, onAdd, blockedIds }) {
  const [t] = useTranslation();
  const { data, error, retry } = usePassage(passage.uuid);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50/70 px-3 py-2 sm:px-4">
      <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-indigo-900">
        <BookOpenIcon className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
        <span className="truncate">{t("test_bank.builder.passage.filter_banner", { name: data?.name || passage.name })}</span>
      </span>
      {data ? (
        <AddPassageButton detail={data} inTest={inTest} onAdd={onAdd} blockedIds={blockedIds} />
      ) : error ? (
        <span role="alert" className="flex flex-wrap items-center gap-1 text-xs text-red-700">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("passages.panel.load_failed")}
          <button type="button" onClick={retry} className={`${linkButtonClass} py-0.5 text-xs max-sm:min-h-11`}>
            {t("passages.panel.retry")}
          </button>
        </span>
      ) : (
        <Spinner />
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <ul aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <li key={i} className="flex items-start gap-3 px-3 py-3 sm:px-4">
          <span className="mt-1 h-4 w-4 rounded bg-slate-100" />
          <div className="flex-1 space-y-2">
            <span className="block h-3 w-28 rounded bg-slate-100 motion-safe:animate-pulse" />
            <span className="block h-3.5 w-3/4 rounded bg-slate-100 motion-safe:animate-pulse" />
            <span className="block h-3 w-1/2 rounded bg-slate-100 motion-safe:animate-pulse" />
          </div>
          <span className="h-8 w-16 rounded-lg bg-slate-100" />
        </li>
      ))}
    </ul>
  );
}

// The row's add / in-test button: a quiet blue "+ Add" (the in-test and pending states keep their own look); an
// icon alone, 44px, on phones.
const rowButtonClass = `inline-flex h-11 min-w-[2.75rem] shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-sm font-medium transition duration-200 sm:h-8 sm:min-w-0 sm:px-2.5 ${focusRing}`;

// One bank question: select, open the preview, add to / remove from the test. `blocked`: its exam takes only
// approved questions into tests and it is not approved, so it can't be added (removing it asks first).
const QuestionRow = React.memo(function QuestionRow({ question, added, pending, blocked, selected, active, onToggleSelect, onOpen, onAdd, onRemove }) {
  const [t] = useTranslation();
  const code = question.code || "";
  const locked = blocked && !added && !pending;
  return (
    <li className={`relative flex items-start gap-3 px-3 py-3 transition duration-150 sm:px-4 ${active ? "bg-blue-50/70" : "hover:bg-slate-50"}`}>
      {active && <span className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" aria-hidden="true" />}
      {/* The label widens the checkbox's touch target to 44px on phones, without moving it. */}
      <label className="-mx-3.5 -mb-3.5 -mt-2.5 flex shrink-0 cursor-pointer p-3.5 has-[:disabled]:cursor-not-allowed sm:m-0 sm:mt-1 sm:p-0">
        <input
          type="checkbox"
          checked={selected && !locked}
          disabled={added || pending || locked}
          onChange={() => onToggleSelect(question)}
          aria-label={locked ? t("test_bank.builder.blocked_code", { code }) : t("test_bank.builder.select_code", { code })}
          title={locked ? t("test_bank.builder.blocked_reason") : undefined}
          className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40"
        />
      </label>
      <button type="button" onClick={() => onOpen(question)} className={`min-w-0 flex-1 rounded-md text-left ${focusRing}`}>
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-xs text-slate-500">{code}</span>
          <StatusBadge status={question.status} />
          <DifficultyBadge level={question.difficulty} />
          <PassageChip passage={question.passage} />
          {/* On phones the action button shows only an icon, so the state is spelled out here. */}
          {added && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 sm:hidden">
              <CheckIcon className="h-3 w-3" aria-hidden="true" />
              {t("test_bank.builder.in_test")}
            </span>
          )}
          {locked && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-500/20 sm:hidden">
              <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
              {t("test_bank.builder.needs_approval")}
            </span>
          )}
        </span>
        <span className={`mt-1 line-clamp-2 break-words text-sm font-medium ${question.name ? "text-slate-900" : "text-slate-500"}`}>
          {question.name || t("test_bank.builder.no_name")}
        </span>
        {questionMeta(question) && <span className="mt-0.5 block truncate text-xs text-slate-500">{questionMeta(question)}</span>}
      </button>
      {/* One button for every state, so keyboard focus stays on it while the change is saved (or after a
          removal it can't undo, when the question can no longer be added). */}
      <button
        type="button"
        onClick={(e) => {
          // The second click of a double click would undo the first one.
          if (pending || locked || e.detail > 1) return;
          if (added) onRemove(question);
          else onAdd(question);
        }}
        aria-disabled={pending || locked || undefined}
        aria-label={
          pending
            ? t("test_bank.builder.saving")
            : added
            ? t("test_bank.builder.remove_code", { code })
            : locked
            ? t("test_bank.builder.blocked_code", { code })
            : t("test_bank.builder.add_code", { code })
        }
        title={added && !pending ? t("test_bank.builder.remove_from_test") : locked ? t("test_bank.builder.blocked_reason") : undefined}
        className={
          pending
            ? `${rowButtonClass} cursor-wait`
            : added
            ? `group/btn ${rowButtonClass} bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20 hover:bg-red-50 hover:text-red-700 hover:ring-red-600/20`
            : locked
            ? `${rowButtonClass} cursor-not-allowed text-slate-500`
            : `${rowButtonClass} text-blue-700 hover:bg-blue-50 hover:text-blue-800`
        }
      >
        {pending ? (
          <Spinner />
        ) : locked ? (
          <>
            <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t("test_bank.builder.needs_approval")}</span>
          </>
        ) : added ? (
          <>
            <CheckIcon className="h-4 w-4 group-hover/btn:hidden group-focus-visible/btn:hidden" aria-hidden="true" />
            <XMarkIcon className="hidden h-4 w-4 group-hover/btn:block group-focus-visible/btn:block" aria-hidden="true" />
            <span className="hidden sm:inline sm:group-hover/btn:hidden sm:group-focus-visible/btn:hidden">{t("test_bank.builder.in_test")}</span>
            <span className="hidden sm:group-hover/btn:inline sm:group-focus-visible/btn:inline">{t("test_bank.builder.remove")}</span>
          </>
        ) : (
          <>
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t("test_bank.builder.add")}</span>
          </>
        )}
      </button>
    </li>
  );
});

/**
 * The question bank side of the builder: folders, search, filters, sorting and server paging (50 per
 * page). Questions already in the test stay listed and are marked (paging and counts stay honest);
 * "Hide added" asks the server to leave them out (exclude_test).
 * onOpen(question, rows) opens the preview; onAdd(questions) / onRemove(question) change the test.
 * `saving`: changes to the test are still being written.
 * Filtered by a passage, the bank lists its questions in the passage's order and offers to add them all.
 * A question its exam does not take into tests yet (isTestBlocked, or refused by the server: `blockedIds`)
 * can't be selected or added. A new `refreshKey` loads the page again (e.g. after such a refusal), and
 * onLoaded(rows, refreshKey) gets every page loaded, with the refreshKey it was asked for.
 */
export default function QuestionBrowser({
  testUuid,
  inTest,
  pendingIds,
  blockedIds,
  refreshKey = 0,
  saving,
  activeUuid,
  wide,
  onOpen,
  onAdd,
  onRemove,
  onSelectionChange,
  onLoaded,
}) {
  const [t] = useTranslation();
  // Phones keep the search and Filters in the toolbar; the sorting goes under Filters (FolderBar's breakpoint).
  const roomy = useMediaQuery("(min-width: 768px)");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [topicValue, setTopicValue] = useState(null);
  const [subtopicValue, setSubtopicValue] = useState(null);
  const [passageValue, setPassageValue] = useState(null);
  const passagesAvailable = usePassagesAvailable();
  const [ordering, setOrdering] = useState("-created");
  const [hideAdded, setHideAdded] = useState(false);
  // Phones: the filters fold under the toolbar (closed at first); wider screens open them over the list.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(() => new Map()); // uuid -> question
  const [lookups, setLookups] = useState({ subjects: [], purposes: [], objectives: [] });
  const [folders, setFolders] = useState(null); // null: the folder API is not available
  const [noFolderCount, setNoFolderCount] = useState(null);
  // The approved status: the builder lists only approved questions. undefined until read (nothing is listed
  // before, so no other status flashes by), null if it cannot be read (then all are listed).
  const [approvedStatus, setApprovedStatus] = useState(undefined);
  const listRef = useRef(null);

  const clearSelection = () => setSelected((prev) => (prev.size ? new Map() : prev));
  // Any new query starts from page 1 with nothing selected (selected rows may no longer be listed).
  const restart = () => {
    setPage(1);
    clearSelection();
  };
  const updateFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    restart();
  };

  const debouncedRef = useRef("");
  useEffect(() => {
    const id = setTimeout(() => {
      const next = search.trim();
      if (next === debouncedRef.current) return;
      debouncedRef.current = next;
      setDebouncedSearch(next);
      restart();
    }, 350);
    return () => clearTimeout(id);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const controller = new AbortController();
    const opts = { params: { page: 1, page_size: 100 }, signal: controller.signal };
    Promise.all([getSubjects(opts), getPurposes(opts), getObjectives(opts)])
      .then(([subjects, purposes, objectives]) =>
        setLookups({ subjects: listOf(subjects), purposes: listOf(purposes), objectives: listOf(objectives) })
      )
      .catch(() => {});
    Promise.all([
      getQuestionFolders({ signal: controller.signal }),
      getQuestions({ params: { page: 1, page_size: 1, folder: "null" }, signal: controller.signal }).catch((err) => {
        if (isCanceledError(err)) throw err;
        return null;
      }),
    ])
      .then(([list, noFolder]) => {
        if (!Array.isArray(list)) return;
        setFolders(list);
        setNoFolderCount(typeof noFolder?.count === "number" ? noFolder.count : null);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const params = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      folder: valueOf(filters.folder),
      subject: valueOf(filters.subject),
      grade: valueOf(filters.grade),
      topic: valueOf(filters.topic),
      subtopic: valueOf(filters.subtopic),
      status: approvedStatus || undefined,
      purpose: valueOf(filters.purpose),
      objective: valueOf(filters.objective),
      passage: valueOf(filters.passage),
      exclude_test: hideAdded ? testUuid : undefined,
    }),
    [debouncedSearch, filters, hideAdded, testUuid, approvedStatus]
  );
  // A passage's questions are listed in its order.
  const listOrdering = filters.passage !== ALL ? "passage_order" : ordering;
  const statusKnown = approvedStatus !== undefined;

  useEffect(() => {
    if (!statusKnown) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getQuestions({ params: { page, page_size: PAGE_SIZE, ordering: listOrdering, ...params }, signal: controller.signal })
      .then((data) => {
        const rows = data?.results || [];
        setResult({ rows, count: data?.count || 0 });
        setLoading(false);
        onLoaded?.(rows, refreshKey);
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        setError(err);
        setLoading(false);
      });
    return () => controller.abort();
  }, [params, listOrdering, page, reloadKey, refreshKey, onLoaded, statusKnown]);

  // Only approved questions go into tests: the approved status, read once.
  useEffect(() => {
    const controller = new AbortController();
    getStatuses({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((res) => setApprovedStatus(listOf(res).find((status) => status.code === "APPROVED")?.uuid || null))
      .catch((err) => {
        if (!isCanceledError(err)) setApprovedStatus(null);
      });
    return () => controller.abort();
  }, []);

  // With "Hide added" the server leaves the test's questions out of every page and count: once a change
  // to them is saved, the page is fetched again (offset paging would otherwise skip rows).
  const testKey = useMemo(() => [...inTest].sort().join(","), [inTest]);
  const savedKeyRef = useRef(testKey);
  useEffect(() => {
    if (saving || testKey === savedKeyRef.current) return;
    savedKeyRef.current = testKey;
    if (hideAdded) setReloadKey((k) => k + 1);
  }, [saving, testKey, hideAdded]);

  const { rows, count } = result;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  // Fewer results than the page we are on (e.g. after hiding added questions): go to the last page.
  useEffect(() => {
    if (!loading && page > pages) setPage(pages);
  }, [loading, page, pages]);

  // A new page starts at its top: the list scrolls on its own on wide screens, the page does below.
  const firstPageRef = useRef(true);
  useEffect(() => {
    if (firstPageRef.current) {
      firstPageRef.current = false;
      return;
    }
    const list = listRef.current;
    if (!list) return;
    if (list.scrollHeight > list.clientHeight + 1) list.scrollTo({ top: 0 });
    else list.scrollIntoView({ block: "start" });
  }, [page]);

  // Questions saved in the test meanwhile cannot stay selected. Ones still being added stay selected
  // (without being offered again), so a failed add keeps the selection.
  useEffect(() => {
    const saved = (id) => inTest.has(id) && !pendingIds.has(id);
    setSelected((prev) => {
      if (![...prev.keys()].some(saved)) return prev;
      return new Map([...prev].filter(([id]) => !saved(id)));
    });
  }, [inTest, pendingIds]);

  // The test does not take these yet: by the rows' own data, or the server refused them (the rows were older
  // than the exam's rule or the question's status; a page loaded since that shows they can be added lifts that).
  const isBlocked = useCallback((question) => isTestBlocked(question) || !!blockedIds?.has(question?.uuid), [blockedIds]);
  // Refused ones leave the selection, so the rest can be added.
  useEffect(() => {
    setSelected((prev) => {
      if (![...prev.values()].some(isBlocked)) return prev;
      return new Map([...prev].filter(([, q]) => !isBlocked(q)));
    });
  }, [isBlocked]);

  // The selected questions that are not in the test (nor being added to it).
  const toAdd = useMemo(() => [...selected.values()].filter((q) => !inTest.has(q.uuid) && !isBlocked(q)), [selected, inTest, isBlocked]);
  // Reported only when it changes: `inTest` is a new set after every write.
  const reportedRef = useRef(null);
  useEffect(() => {
    const key = toAdd.map((q) => q.uuid).join(",");
    if (key === reportedRef.current) return;
    reportedRef.current = key;
    onSelectionChange?.(toAdd);
  }, [toAdd, onSelectionChange]);

  const toggleSelect = useCallback((question) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(question.uuid)) next.delete(question.uuid);
      else next.set(question.uuid, question);
      return next;
    });
  }, []);

  const addable = rows.filter((q) => !inTest.has(q.uuid) && !pendingIds.has(q.uuid) && !isBlocked(q));
  const pageSelected = addable.filter((q) => selected.has(q.uuid)).length;
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageSelected === addable.length) addable.forEach((q) => next.delete(q.uuid));
      else addable.forEach((q) => next.set(q.uuid, q));
      return next;
    });

  // The selection is not cleared here: the questions leave it once saved, and stay in it if the add fails.
  const addSelected = () => onAdd(toAdd);

  const handleOpen = useCallback((question) => onOpen(question, rows), [onOpen, rows]);
  const handleAdd = useCallback((question) => onAdd([question]), [onAdd]);

  const gradeOptions = useMemo(
    () => [
      { value: ALL, label: t("questions_page.filters.all_grades") },
      { value: -1, label: t("questions_page.filters.teachers") },
      ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: t("questions_page.filters.grade_n", { grade: i + 1 }) })),
    ],
    [t]
  );
  const subjectOptions = useMemo(
    () => [
      { value: ALL, label: t("questions_page.filters.all_subjects") },
      ...lookups.subjects.map((s) => ({ value: s.uuid, label: s.name })),
      ...(folders ? [{ value: "null", label: t("folders.no_subject") }] : []),
    ],
    [lookups.subjects, folders, t]
  );
  const purposeOptions = useMemo(
    () => [{ value: ALL, label: t("questions_page.filters.all_terms") }, ...lookups.purposes.map((p) => ({ value: p.uuid, label: p.name }))],
    [lookups.purposes, t]
  );
  const objectiveOptions = useMemo(
    () => [{ value: ALL, label: t("questions_page.filters.all_objectives") }, ...lookups.objectives.map((o) => ({ value: o.uuid, label: o.name }))],
    [lookups.objectives, t]
  );
  const orderingOptions = useMemo(
    () => [
      { value: "-created", label: t("test_bank.builder.sort_newest") },
      { value: "created", label: t("test_bank.builder.sort_oldest") },
      { value: "name", label: t("test_bank.builder.sort_name") },
    ],
    [t]
  );

  const fetchTopics = useCallback(
    (text) =>
      getTopics({
        params: {
          page: 1,
          page_size: 100,
          subject: filters.subject !== ALL && filters.subject !== "null" ? filters.subject : undefined,
          grade: valueOf(filters.grade),
          search: text || undefined,
        },
      }).then(listOf),
    [filters.subject, filters.grade]
  );
  const fetchSubtopics = useCallback(
    (text) =>
      filters.topic === ALL
        ? Promise.resolve([])
        : getSubtopics({ params: { page: 1, page_size: 100, topic: filters.topic, search: text || undefined } }).then(listOf),
    [filters.topic]
  );
  const fetchPassages = useCallback((text) => getPassages({ params: { page: 1, page_size: 100, search: text || undefined } }).then(listOf), []);

  const activeFilters = ["subject", "grade", "topic", "subtopic", "purpose", "objective", "passage"].filter((k) => filters[k] !== ALL).length;
  const anyQuery = activeFilters > 0 || filters.folder !== ALL || !!debouncedSearch || hideAdded;
  const clearAll = () => {
    setSearch("");
    setDebouncedSearch("");
    debouncedRef.current = "";
    setFilters(EMPTY_FILTERS);
    setTopicValue(null);
    setSubtopicValue(null);
    setPassageValue(null);
    setHideAdded(false);
    restart();
  };
  // The folders as one choice (the list keeps the height a row of folder buttons took): all, no folder, then each
  // exam folder whole and its subjects. Folders are browsed on the Folders page.
  const folderOptions = useMemo(() => {
    if (!folders) return [];
    const exams = folders.filter((f) => !f.parent);
    return [
      {
        label: "",
        options: [
          { value: ALL, label: t("folders.all_questions") },
          { value: "null", label: t("folders.no_folder"), count: noFolderCount },
        ],
      },
      ...exams.map((exam) => ({
        label: exam.name,
        options: [
          { value: exam.uuid, label: exam.name, menuLabel: t("test_bank.builder.folder_whole"), count: exam.questions_count },
          ...folders
            .filter((sub) => sub.parent === exam.uuid)
            .map((sub) => ({ value: sub.uuid, label: `${exam.name} › ${sub.name}`, menuLabel: sub.name, count: sub.questions_count })),
        ],
      })),
    ];
  }, [folders, noFolderCount, t]);
  const folderSelect = folders && (
    <Select
      className="w-full min-w-0 sm:w-64"
      ariaLabel={t("folders.label")}
      options={folderOptions}
      value={filters.folder}
      onChange={(o) => updateFilters({ folder: o?.value ?? ALL })}
      isSearchable
      formatOptionLabel={(option, { context }) =>
        context === "menu" ? (
          <span className="flex items-baseline justify-between gap-3">
            <span className="min-w-0">{option.menuLabel || option.label}</span>
            {typeof option.count === "number" && <span className="shrink-0 text-xs tabular-nums text-slate-400">{option.count}</span>}
          </span>
        ) : (
          option.label
        )
      }
    />
  );

  // Only the filters (the folder, the search and "hide added" stay).
  const clearFilters = () => {
    setFilters((prev) => ({ ...EMPTY_FILTERS, folder: prev.folder }));
    setTopicValue(null);
    setSubtopicValue(null);
    setPassageValue(null);
    restart();
  };
  const from = count ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, count);
  // The sorting: in the toolbar, on phones first under Filters.
  const sortSelect = (
    <Select
      className={roomy ? "w-56 min-w-0" : "min-w-0"}
      options={orderingOptions}
      value={ordering}
      onChange={(o) => {
        setOrdering(o?.value || "-created");
        restart();
      }}
      isDisabled={filters.passage !== ALL}
      isSearchable={false}
    />
  );

  // The filters: in a panel over the list from 768px, folded under the toolbar on phones (with the sorting).
  const filterFields = (
    <>
      <Select
        className="min-w-0"
        options={gradeOptions}
        value={filters.grade}
        onChange={(o) => {
          setTopicValue(null);
          setSubtopicValue(null);
          updateFilters({ grade: o?.value ?? ALL, topic: ALL, subtopic: ALL });
        }}
        isSearchable={false}
      />
      <Select
        className="min-w-0"
        options={subjectOptions}
        value={filters.subject}
        onChange={(o) => {
          setTopicValue(null);
          setSubtopicValue(null);
          updateFilters({ subject: o?.value || ALL, topic: ALL, subtopic: ALL });
        }}
        isSearchable
      />
      <AsyncSearchSelect
        key={`topic-${filters.subject}-${filters.grade}`}
        className="min-w-0"
        placeholder={t("questions_page.filters.all_topics")}
        value={topicValue}
        isClearable
        fetchOptions={fetchTopics}
        onChange={(v) => {
          setTopicValue(v);
          setSubtopicValue(null);
          updateFilters({ topic: v ? v.value : ALL, subtopic: ALL });
        }}
      />
      <AsyncSearchSelect
        key={`subtopic-${filters.topic}`}
        className="min-w-0"
        placeholder={t("questions_page.filters.all_subtopics")}
        value={subtopicValue}
        isClearable
        isDisabled={filters.topic === ALL}
        fetchOptions={fetchSubtopics}
        onChange={(v) => {
          setSubtopicValue(v);
          updateFilters({ subtopic: v ? v.value : ALL });
        }}
      />
      <Select
        className="min-w-0"
        options={purposeOptions}
        value={filters.purpose}
        onChange={(o) => updateFilters({ purpose: o?.value || ALL })}
        isSearchable={false}
      />
      <Select
        className="min-w-0"
        options={objectiveOptions}
        value={filters.objective}
        onChange={(o) => updateFilters({ objective: o?.value || ALL })}
        isSearchable={false}
      />
      {(passagesAvailable || filters.passage !== ALL) && (
        <AsyncSearchSelect
          className="min-w-0"
          placeholder={`${t("test_bank.builder.passage.filter")}: ${t("test_bank.builder.passage.filter_placeholder")}`}
          value={passageValue}
          isClearable
          fetchOptions={fetchPassages}
          onChange={(v) => {
            // "Add whole passage" adds what is linked now, not a cached list of its questions.
            if (v?.value) forgetPassage(v.value);
            setPassageValue(v);
            updateFilters({ passage: v ? v.value : ALL });
          }}
        />
      )}
    </>
  );

  return (
    <section aria-labelledby="builder-bank-title" className="flex min-h-0 flex-col gap-3 xl:h-full">
      <h2 id="builder-bank-title" className="sr-only">
        {t("test_bank.builder.tab_bank")}
      </h2>
      <div className={`${cardClass} flex min-h-0 flex-1 flex-col`}>
        <div className="space-y-3 border-b border-slate-100 p-3 sm:p-4">
          <div className="relative flex flex-wrap items-center gap-2">
            {folderSelect}
            <div className="relative min-w-[12rem] flex-1">
              <label htmlFor="builder-search" className="sr-only">
                {t("test_bank.builder.search_label")}
              </label>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                id="builder-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("test_bank.editor.search_placeholder")}
                className="h-11 w-full rounded-lg border-slate-300 pl-9 pr-9 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-500 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 max-sm:pr-11 sm:h-10 [&::-webkit-search-cancel-button]:hidden"
              />
              {search.trim() !== debouncedSearch ? (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner className="h-4 w-4" />
                </span>
              ) : (
                search && (
                  // 44px on phones: the field's height, at its right end.
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label={t("test_bank.builder.clear_search")}
                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 max-sm:right-0 max-sm:p-3.5 ${focusRing}`}
                  >
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                )
              )}
            </div>
            {roomy && sortSelect}
            {roomy ? (
              <Popover>
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      className={`inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium shadow-sm ring-1 ring-inset transition duration-200 ${focusRing} ${
                        open || activeFilters ? "bg-blue-50 text-blue-700 ring-blue-600/20" : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                      {t("test_bank.filters.title")}
                      {activeFilters > 0 && (
                  <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-white">{activeFilters}</span>
                )}
                    </Popover.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-75"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel
                        id="builder-filters"
                        className="absolute inset-x-0 top-full z-30 mt-2 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-slate-900/10 focus:outline-none"
                      >
                        {/* react-select marks every Escape as handled, which Headless UI then ignores: the one with no
                            open menu to close closes the panel (on a plain element, outside Headless UI's handlers). */}
                        <div
                          className="grid grid-cols-2 gap-3 2xl:grid-cols-4"
                          onKeyDown={(event) => {
                            if (event.key === "Escape" && event.target.getAttribute?.("aria-expanded") !== "true") close();
                          }}
                        >
                          {filterFields}
                        </div>
                        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
                          {activeFilters > 0 && (
                            <button type="button" onClick={clearFilters} className={linkButtonClass}>
                              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                              {t("test_bank.filters.clear")}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => close()}
                            className={`inline-flex h-9 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-700 ${focusRing}`}
                          >
                            {t("test_bank.filters.done")}
                          </button>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            ) : (
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
                aria-controls="builder-filters"
                className={`inline-flex h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium shadow-sm ring-1 ring-inset transition duration-200 sm:h-10 ${focusRing} ${
                  filtersOpen || activeFilters ? "bg-blue-50 text-blue-700 ring-blue-600/20" : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
                }`}
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                {t("test_bank.filters.title")}
                {activeFilters > 0 && (
                  <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-white">{activeFilters}</span>
                )}
              </button>
            )}
          </div>

          {!roomy && filtersOpen && (
            <div id="builder-filters" className="grid grid-cols-1 gap-3">
              {sortSelect}
              {filterFields}
            </div>
          )}

          {/* On phones the checkboxes' labels and Clear are 44px tall (touch targets), so their lines need no gap. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm max-sm:gap-y-0">
            <label className="inline-flex cursor-pointer select-none items-center gap-2 text-slate-600 max-sm:min-h-11">
              <input
                type="checkbox"
                checked={addable.length > 0 && pageSelected === addable.length}
                aria-label={t("test_bank.builder.select_page")}
                ref={(el) => {
                  if (el) el.indeterminate = pageSelected > 0 && pageSelected < addable.length;
                }}
                disabled={!addable.length}
                onChange={togglePage}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0 disabled:opacity-40"
              />
              <span>
                {t("test_bank.builder.found")} <span className="font-semibold tabular-nums text-slate-900">{count}</span>
              </span>
            </label>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 max-sm:gap-y-0">
              <label className="inline-flex cursor-pointer select-none items-center gap-2 text-slate-600 max-sm:min-h-11">
                <input
                  type="checkbox"
                  checked={hideAdded}
                  onChange={(e) => {
                    setHideAdded(e.target.checked);
                    restart();
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
                />
                {t("test_bank.builder.hide_added")}
              </label>
              {anyQuery && (
                <button type="button" onClick={clearAll} className={`${linkButtonClass} max-sm:min-h-11`}>
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  {t("test_bank.filters.clear")}
                </button>
              )}
            </div>
          </div>
        </div>

        {filters.passage !== ALL && (
          <PassageFilterBanner
            passage={{ uuid: filters.passage, name: passageValue?.label || "" }}
            inTest={inTest}
            onAdd={onAdd}
            blockedIds={blockedIds}
          />
        )}

        {toAdd.length > 0 && (
          // Sticky under the builder's tabs (60px tall on phones, 52px from sm; none from xl).
          <div className="sticky top-[3.75rem] z-[2] flex flex-wrap items-center justify-between gap-2 border-b border-blue-100 bg-blue-50 px-3 py-2 sm:top-[3.25rem] sm:px-4 xl:top-0">
            <span className="text-sm font-medium text-blue-900" aria-live="polite">
              {t("test_bank.builder.selected_count", { count: toAdd.length })}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={clearSelection} className={`${linkButtonClass} max-sm:min-h-11`}>
                {t("test_bank.editor.clear_selection")}
              </button>
              <button
                type="button"
                onClick={addSelected}
                className={`inline-flex h-11 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 sm:h-9 ${focusRing}`}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                {t("test_bank.editor.add_questions_count", { count: toAdd.length })}
              </button>
            </div>
          </div>
        )}

        <div ref={listRef} className="relative min-h-0 flex-1 xl:overflow-y-auto" aria-busy={loading}>
          {loading && rows.length > 0 && (
            <span className="absolute inset-x-0 top-0 z-[1] h-0.5 overflow-hidden bg-blue-100" aria-hidden="true">
              <span className="block h-full w-1/3 bg-blue-600 motion-safe:animate-pulse" />
            </span>
          )}
          {error ? (
            <div role="alert" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-400" aria-hidden="true" />
              <p className="text-sm text-slate-600">{t("test_bank.builder.bank_error")}</p>
              <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={`${linkButtonClass} max-sm:min-h-11`}>
                {t("test_bank.builder.retry")}
              </button>
            </div>
          ) : loading && rows.length === 0 ? (
            <SkeletonRows />
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
              <MagnifyingGlassIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
              <p className="text-sm font-medium text-slate-900">{t("test_bank.editor.no_questions_found")}</p>
              <p className="text-sm text-slate-500">{t("test_bank.editor.try_changing_filters")}</p>
              {anyQuery && (
                <button type="button" onClick={clearAll} className={`${linkButtonClass} mt-1 max-sm:min-h-11`}>
                  {t("test_bank.filters.clear")}
                </button>
              )}
            </div>
          ) : (
            <ul className={`divide-y divide-slate-100 transition-opacity duration-150 ${loading ? "opacity-60" : ""}`}>
              {rows.map((q) => (
                <QuestionRow
                  key={q.uuid}
                  question={q}
                  added={inTest.has(q.uuid)}
                  pending={pendingIds.has(q.uuid)}
                  blocked={isBlocked(q)}
                  selected={selected.has(q.uuid)}
                  active={activeUuid === q.uuid}
                  onToggleSelect={toggleSelect}
                  onOpen={handleOpen}
                  onAdd={handleAdd}
                  onRemove={onRemove}
                />
              ))}
            </ul>
          )}
        </div>

        {count > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2 text-sm text-slate-500 sm:px-4">
            <span className="tabular-nums">{t("test_bank.editor.range", { from, to, total: count })}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label={t("buttons.previous")}
                className={`${iconButtonClass} max-sm:h-11 max-sm:w-11`}
              >
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="px-1 tabular-nums">{t("test_bank.common.page_of", { page, pages })}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                aria-label={t("buttons.next")}
                className={`${iconButtonClass} max-sm:h-11 max-sm:w-11`}
              >
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
